import React, { useState, useEffect } from 'react';
import { Calendar, MapPin, Gift, Hospital, Award, Share2, Droplet, ChevronDown, ChevronUp, Eye, FileText, Download } from 'lucide-react';
import { motion } from 'framer-motion';
import { toast } from 'react-toastify';
import api from '../../../config/api';

const DonationHistory = ({ handleDownloadCertificate }) => {
  const [donationHistory, setDonationHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [bloodGroupFilter, setBloodGroupFilter] = useState('');
  const [timeFilter, setTimeFilter] = useState('');
  const [expandedDonation, setExpandedDonation] = useState(null);
  const [sharingDonation, setSharingDonation] = useState(false);

  // Fetch donation history
  useEffect(() => {
    const fetchDonationHistory = async () => {
      try {
        setLoading(true);
        // Get donor profile which contains donation history
        const response = await api.get('/donors/me');
        
        // Process both direct donations and accepted requests that were donated
        const directDonations = response.data.donationHistory.map(donation => ({
          donationId: donation._id,
          date: donation.date,
          hospitalName: donation.bloodBank,
          location: donation.location,
          bloodGroup: response.data.bloodType,
          amount: donation.amount,
          status: 'Completed',
          certificate: 'Available',
          type: 'direct',
          rawData: donation
        }));
        
        // Get completed donations from accepted requests
        const requestDonations = response.data.acceptedRequests
          .filter(req => req.status === 'donated')
          .map(req => ({
            donationId: req._id,
            date: req.donationDate,
            recipientName: req.request?.patientName || 'Unknown',
            hospitalName: req.request?.hospital?.name || '',
            bloodGroup: response.data.bloodType,
            status: 'Completed',
            certificate: 'Available',
            type: 'request',
            requestId: req.request?._id,
            notes: req.notes,
            donationProofPhoto: req.donationProofPhoto,
            requesterFeedback: req.requesterFeedback,
            requesterDetails: {
              name: req.request?.requester?.name || 'Not available',
              email: req.request?.requester?.email || 'Not available',
              phone: req.request?.requester?.phone || 'Not available',
              relation: req.request?.requesterRelation || ''
            },
            rawData: req
          }));
        
        // Combine both types of donations and sort by date (newest first)
        const allDonations = [...directDonations, ...requestDonations]
          .sort((a, b) => new Date(b.date) - new Date(a.date));
          
        setDonationHistory(allDonations);
      } catch (err) {
        console.error('Error fetching donation history:', err);
        setError('Failed to load donation history. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchDonationHistory();
  }, []);

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return 'Not available';
    
    const options = { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  // Toggle expanded view for a donation
  const toggleExpand = (donationId) => {
    if (expandedDonation === donationId) {
      setExpandedDonation(null);
    } else {
      setExpandedDonation(donationId);
    }
  };

  // Handle sharing donation details
  const handleShareDetails = (donation) => {
    setSharingDonation(true);
    
    // Create a shareable text with donation details
    const shareText = `
🩸 Blood Donation Certificate 🩸

Date: ${formatDate(donation.date)}
Blood Group: ${donation.bloodGroup}
Hospital/Blood Bank: ${donation.hospitalName}
${donation.location ? `Location: ${donation.location}` : ''}
${donation.amount ? `Amount: ${donation.amount} ml` : ''}
Status: ${donation.status}

Thank you for being a lifesaver! 
#BloodHero #DonateBloodSaveLife
    `.trim();
    
    try {
      // Try to use the Web Share API if available
      if (navigator.share) {
        navigator.share({
          title: 'My Blood Donation Certificate',
          text: shareText,
        })
        .then(() => {
          toast.success('Donation details shared successfully!');
        })
        .catch((error) => {
          console.error('Error sharing:', error);
          // Fallback to clipboard if sharing fails
          copyToClipboard(shareText);
        })
        .finally(() => {
          setSharingDonation(false);
        });
      } else {
        // Fallback to clipboard if Web Share API is not available
        copyToClipboard(shareText);
        setSharingDonation(false);
      }
    } catch (error) {
      console.error('Error sharing donation details:', error);
      toast.error('Failed to share donation details');
      setSharingDonation(false);
    }
  };

  // Helper function to copy text to clipboard
  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text)
      .then(() => {
        toast.success('Donation details copied to clipboard!');
      })
      .catch((err) => {
        console.error('Failed to copy text: ', err);
        toast.error('Failed to copy to clipboard');
      });
  };

  // Handle viewing proof of donation in full screen
  const handleViewProof = (imageUrl) => {
    if (!imageUrl) return;
    
    // Create a modal to view the image
    const modal = document.createElement('div');
    modal.style.position = 'fixed';
    modal.style.top = '0';
    modal.style.left = '0';
    modal.style.width = '100%';
    modal.style.height = '100%';
    modal.style.backgroundColor = 'rgba(0, 0, 0, 0.9)';
    modal.style.display = 'flex';
    modal.style.alignItems = 'center';
    modal.style.justifyContent = 'center';
    modal.style.zIndex = '9999';
    modal.style.cursor = 'pointer';
    
    const img = document.createElement('img');
    img.src = imageUrl;
    img.style.maxWidth = '90%';
    img.style.maxHeight = '90%';
    img.style.objectFit = 'contain';
    
    modal.appendChild(img);
    document.body.appendChild(modal);
    
    // Close modal when clicked
    modal.addEventListener('click', () => {
      document.body.removeChild(modal);
    });
  };
  
  // Handle downloading proof of donation
  const handleDownloadProof = (imageUrl, donationId) => {
    if (!imageUrl) return;
    
    // Create a link to download the image
    const link = document.createElement('a');
    link.href = imageUrl;
    link.download = `donation_proof_${donationId}.jpg`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    toast.success('Proof of donation downloaded successfully!');
  };

  // Filter donations based on selected filters
  const filteredDonations = donationHistory.filter(donation => {
    // Filter by blood group
    if (bloodGroupFilter && donation.bloodGroup !== bloodGroupFilter) {
      return false;
    }
    
    // Filter by time
    if (timeFilter) {
      const donationDate = new Date(donation.date);
      const now = new Date();
      const monthsDiff = (now.getFullYear() - donationDate.getFullYear()) * 12 + 
                         now.getMonth() - donationDate.getMonth();
      
      if (timeFilter === 'last_month' && monthsDiff > 1) return false;
      if (timeFilter === 'last_3_months' && monthsDiff > 3) return false;
      if (timeFilter === 'last_6_months' && monthsDiff > 6) return false;
      if (timeFilter === 'last_year' && monthsDiff > 12) return false;
    }
    
    return true;
  });

  return (
    <div className="space-y-6">
      {/* Header Section */}
      <div className="bg-white rounded-xl shadow-sm p-6">
        <div className="flex flex-col md:flex-row gap-4 items-start md:items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Donation History</h2>
            <p className="text-gray-500 mt-1">Track your blood donation journey and impact</p>
          </div>
          
          {/* Filter Controls */}
          <div className="flex flex-wrap gap-3">
            <select 
              className="px-4 py-2 bg-gray-50 border-none rounded-lg text-gray-600 text-sm focus:ring-2 focus:ring-red-500"
              value={bloodGroupFilter}
              onChange={(e) => setBloodGroupFilter(e.target.value)}
            >
              <option value="">All Blood Groups</option>
              <option value="A+">A+</option>
              <option value="A-">A-</option>
              <option value="B+">B+</option>
              <option value="B-">B-</option>
              <option value="AB+">AB+</option>
              <option value="AB-">AB-</option>
              <option value="O+">O+</option>
              <option value="O-">O-</option>
            </select>
            
            <select 
              className="px-4 py-2 bg-gray-50 border-none rounded-lg text-gray-600 text-sm focus:ring-2 focus:ring-red-500"
              value={timeFilter}
              onChange={(e) => setTimeFilter(e.target.value)}
            >
              <option value="">All Time</option>
              <option value="last_month">Last Month</option>
              <option value="last_3_months">Last 3 Months</option>
              <option value="last_6_months">Last 6 Months</option>
              <option value="last_year">Last Year</option>
            </select>
          </div>
        </div>
      </div>

      {/* Loading State */}
      {loading && (
        <div className="bg-white rounded-xl shadow-sm p-8 text-center">
          <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4 animate-pulse">
            <Gift className="w-8 h-8 text-gray-400" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900">Loading donation history...</h3>
        </div>
      )}

      {/* Error State */}
      {error && !loading && (
        <div className="bg-white rounded-xl shadow-sm p-8 text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Gift className="w-8 h-8 text-red-400" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900">Error</h3>
          <p className="mt-2 text-sm text-gray-500">{error}</p>
        </div>
      )}

      {/* Donations Grid */}
      {!loading && !error && (
        <div className="grid grid-cols-1 gap-4">
          {filteredDonations.map((donation, index) => (
            <motion.div
              key={donation.donationId}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              className="bg-white rounded-xl shadow-sm overflow-hidden"
            >
              <div className="p-6">
                <div className="flex flex-col md:flex-row gap-6">
                  {/* Left Section - Basic Info */}
                  <div className="flex-1">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 bg-red-50 rounded-xl flex items-center justify-center">
                        <Gift className="w-6 h-6 text-red-600" />
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <h3 className="font-semibold text-gray-900">
                            {donation.type === 'direct' ? 'Direct Donation' : 'Request Donation'}
                          </h3>
                          <span className="px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                            {donation.status}
                          </span>
                        </div>
                        {donation.recipientName && (
                          <p className="text-sm text-gray-500 mt-1">
                            Recipient: {donation.recipientName}
                          </p>
                        )}
                      </div>
                    </div>

                    <div className="mt-4 grid grid-cols-2 gap-4">
                      <div className="flex items-center gap-2">
                        <Calendar className="w-4 h-4 text-gray-400" />
                        <span className="text-sm text-gray-600">{formatDate(donation.date)}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Droplet className="w-4 h-4 text-gray-400" />
                        <span className="text-sm text-gray-600">{donation.bloodGroup}</span>
                      </div>
                      {donation.hospitalName && (
                        <div className="flex items-center gap-2">
                          <Hospital className="w-4 h-4 text-gray-400" />
                          <span className="text-sm text-gray-600">{donation.hospitalName}</span>
                        </div>
                      )}
                      {donation.amount && (
                        <div className="flex items-center gap-2">
                          <Droplet className="w-4 h-4 text-gray-400" />
                          <span className="text-sm text-gray-600">{donation.amount} ml</span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Right Section - Actions */}
                  <div className="flex flex-col sm:flex-row md:flex-col gap-3 min-w-[200px] justify-center">
                    {donation.certificate === 'Available' && (
                      <button 
                        onClick={() => handleDownloadCertificate(donation)}
                        className="w-full px-4 py-2 bg-red-50 text-red-600 rounded-lg hover:bg-red-100 transition-colors flex items-center justify-center gap-2"
                      >
                        <Award className="w-4 h-4" />
                        Download Certificate
                      </button>
                    )}
                    <button 
                      onClick={() => handleShareDetails(donation)}
                      disabled={sharingDonation}
                      className="w-full px-4 py-2 border border-gray-200 text-gray-600 rounded-lg hover:bg-gray-50 transition-colors flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <Share2 className="w-4 h-4" />
                      {sharingDonation ? 'Sharing...' : 'Share Details'}
                    </button>
                    <button 
                      onClick={() => toggleExpand(donation.donationId)}
                      className="w-full px-4 py-2 border border-gray-200 text-gray-600 rounded-lg hover:bg-gray-50 transition-colors flex items-center justify-center gap-2"
                    >
                      {expandedDonation === donation.donationId ? (
                        <>
                          <ChevronUp className="w-4 h-4" />
                          Hide Details
                        </>
                      ) : (
                        <>
                          <ChevronDown className="w-4 h-4" />
                          View Details
                        </>
                      )}
                    </button>
                  </div>
                </div>

                {/* Expanded Details Section */}
                {expandedDonation === donation.donationId && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    transition={{ duration: 0.3 }}
                    className="mt-6 pt-6 border-t border-gray-100"
                  >
                    <h4 className="font-semibold text-gray-900 mb-4">Donation Details</h4>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      {/* Left Column */}
                      <div className="space-y-4">
                        {/* Placeholder for future content if needed */}
                      </div>

                      {/* Right Column */}
                      <div className="space-y-4">
                        {/* Proof of Donation */}
                        {donation.donationProofPhoto && (
                          <div>
                            <h5 className="text-sm font-medium text-gray-700 mb-2">Proof of Donation</h5>
                            <div className="bg-gray-50 p-4 rounded-lg">
                              <div className="flex gap-3">
                                <button
                                  onClick={() => handleViewProof(donation.donationProofPhoto)}
                                  className="flex-1 px-4 py-2 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 transition-colors flex items-center justify-center gap-2 text-sm"
                                >
                                  <Eye className="w-4 h-4" />
                                  View Image
                                </button>
                                <button
                                  onClick={() => handleDownloadProof(donation.donationProofPhoto, donation.donationId)}
                                  className="flex-1 px-4 py-2 bg-green-50 text-green-600 rounded-lg hover:bg-green-100 transition-colors flex items-center justify-center gap-2 text-sm"
                                >
                                  <Download className="w-4 h-4" />
                                  Download
                                </button>
                              </div>
                            </div>
                          </div>
                        )}
                        
                        {/* Requester Details (for request-based donations) */}
                        {donation.type === 'request' && (
                          <div>
                            <h5 className="text-sm font-medium text-gray-700 mb-2">Requester Details</h5>
                            <div className="bg-gray-50 p-4 rounded-lg">
                              <div className="flex items-center gap-3 mb-3">
                                <div className="w-10 h-10 bg-red-50 rounded-full flex items-center justify-center">
                                  <Droplet className="w-5 h-5 text-red-600" />
                                </div>
                                <div>
                                  <h4 className="font-medium text-gray-900">Blood Group {donation.bloodGroup}</h4>
                                  <p className="text-sm text-gray-500">{donation.requesterDetails?.name || 'Not available'}</p>
                                </div>
                                <div className="ml-auto">
                                  <span className="px-3 py-1 bg-red-50 text-red-600 rounded-full text-sm font-medium">
                                    {donation.amount ? `${donation.amount} ml` : '1 unit'}
                                  </span>
                                </div>
                              </div>
                              
                              <div className="space-y-3 text-sm">
                                <div className="flex items-start gap-3">
                                  <Calendar className="w-5 h-5 text-gray-400 mt-0.5 flex-shrink-0" />
                                  <div>
                                    <p className="text-gray-500">Created: {formatDate(donation.rawData?.request?.createdAt || donation.date)}</p>
                                    <div className="flex items-center gap-1 mt-1">
                                      <div className="w-5 h-5 bg-blue-50 rounded-full flex items-center justify-center">
                                        <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-blue-500">
                                          <polyline points="20 6 9 17 4 12"></polyline>
                                        </svg>
                                      </div>
                                      <p className="text-gray-500">
                                        Accepted on<br />
                                        {formatDate(donation.rawData?.acceptedAt || donation.date)}
                                      </p>
                                    </div>
                                  </div>
                                </div>
                                
                                <div className="flex items-center gap-3">
                                  <Calendar className="w-5 h-5 text-gray-400 flex-shrink-0" />
                                  <p className="text-gray-500">
                                    Donation Date<br />
                                    {formatDate(donation.date).split(',')[0]}
                                  </p>
                                </div>
                                
                                <div className="flex items-start gap-3">
                                  <MapPin className="w-5 h-5 text-gray-400 mt-0.5 flex-shrink-0" />
                                  <p className="text-gray-500">
                                    {donation.rawData?.request?.address || donation.location || '3852, Ward No 5, Dhani Shyam Lal, Rajeev nagar, Hisar, Haryana 125001, India'}
                                  </p>
                                </div>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Certificate Information */}
                    <div className="mt-6">
                      <h5 className="text-sm font-medium text-gray-700 mb-2">Certificate Information</h5>
                      <div className="bg-gray-50 p-4 rounded-lg">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <FileText className="w-5 h-5 text-gray-400" />
                            <span className="text-sm font-medium text-gray-900">
                              Donation Certificate
                            </span>
                          </div>
                          <span className={`text-sm font-medium ${donation.certificate === 'Available' ? 'text-green-600' : 'text-gray-500'}`}>
                            {donation.certificate}
                          </span>
                        </div>
                        {donation.certificate === 'Available' && (
                          <button 
                            onClick={() => handleDownloadCertificate(donation)}
                            className="mt-3 w-full px-4 py-2 bg-red-50 text-red-600 rounded-lg hover:bg-red-100 transition-colors flex items-center justify-center gap-2"
                          >
                            <Award className="w-4 h-4" />
                            Download Certificate
                          </button>
                        )}
                      </div>
                    </div>
                  </motion.div>
                )}
              </div>
            </motion.div>
          ))}
        </div>
      )}

      {/* Empty State */}
      {!loading && !error && filteredDonations.length === 0 && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="bg-white rounded-xl shadow-sm p-8 text-center"
        >
          <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Gift className="w-8 h-8 text-gray-400" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900">No Donation History</h3>
          <p className="mt-2 text-sm text-gray-500">
            {bloodGroupFilter || timeFilter 
              ? 'No donations match your current filters. Try adjusting your filters to see more results.'
              : 'Your blood donation history will appear here once you make your first donation.'}
          </p>
        </motion.div>
      )}

      {/* Pagination */}
      {!loading && !error && filteredDonations.length > 0 && (
        <div className="bg-white rounded-xl shadow-sm p-4">
          <div className="flex items-center justify-between">
            <p className="text-sm text-gray-600">
              Showing <span className="font-medium">1</span> to{" "}
              <span className="font-medium">{filteredDonations.length}</span> of{" "}
              <span className="font-medium">{filteredDonations.length}</span> donations
            </p>
            <div className="flex gap-2">
              <button 
                disabled={true}
                className="px-4 py-2 border border-gray-200 rounded-lg text-gray-600 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              <button 
                disabled={true}
                className="px-4 py-2 border border-gray-200 rounded-lg text-gray-600 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DonationHistory;